import { api } from '@/lib/api'

/** Same shape `/auth/login` returns; persisted by `useAuth`. */
export interface BiometricAuthPayload {
  user: Record<string, unknown>
  tokens: { accessToken: string; refreshToken: string; expiresAt?: string }
}

export interface BiometricRegisterResponse {
  credential: { id: string; userId: string; deviceId: string; platform: string; createdAt: string }
  auth?: BiometricAuthPayload
}

export interface BiometricChallenge {
  challengeId: string
  /** Base64, 32 bytes. */
  challenge: string
  expiresInSeconds: number
}

/** Mobile `AuthBloc` fallback when the backend gives no message. */
const FALLBACK_ERROR = 'Biometric authentication failed'

/** POST /api/auth/biometric/register (JWT required) — `publicKey` is the base64 SPKI of the device key. */
export async function registerBiometricDevice(params: {
  deviceId: string
  publicKey: string
}): Promise<BiometricRegisterResponse> {
  const res = await api.post('/auth/biometric/register', { ...params, platform: 'web' })
  return res.data
}

/** POST /api/auth/biometric/challenge (anonymous). */
export async function requestBiometricChallenge(deviceId: string): Promise<BiometricChallenge> {
  const res = await api.post('/auth/biometric/challenge', { deviceId })
  return res.data
}

/** POST /api/auth/biometric/authenticate (anonymous). Throws the backend `error` when it rejects. */
export async function authenticateBiometric(params: {
  deviceId: string
  challengeId: string
  signedChallenge: string
}): Promise<BiometricAuthPayload> {
  const res = await api.post('/auth/biometric/authenticate', params)
  const { success, auth, error } = res.data as { success: boolean; auth?: BiometricAuthPayload; error?: string }
  if (!success || !auth) throw new Error(error || FALLBACK_ERROR)
  return auth
}
